
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface TherapyModality {
  id: string;
  name_cn: string;
  name_en: string;
}

interface ServiceTherapyTag {
  id: string;
  is_primary: boolean;
  therapy_modality: TherapyModality;
}

interface Service {
  id: string;
  service_name: string;
  description: string | null;
  session_instructions: string | null;
  price: number;
  duration_minutes: number;
  formats_available: string[];
  is_active: boolean;
  currency: string;
  service_photo: string | null;
  is_free_session: boolean;
  created_at: string;
  updated_at: string;
  parental_consent_required: boolean;
  adults_only: boolean;
  service_therapy_tags?: ServiceTherapyTag[];
  custom_therapy_tags?: string[];
}

export const useServicesManager = (userId: string) => {
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');
  const { user, activeRole } = useAuth();

  const fetchServices = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('services')
        .select(`
          *,
          service_therapy_tags (
            id,
            is_primary,
            therapy_modality:therapy_modalities (
              id,
              name_en,
              name_cn
            )
          )
        `)
        .eq('practitioner_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast({
        variant: "destructive",
        title: t('servicesManagement.actions.failedToLoadServices'),
        description: t('servicesManagement.actions.failedToLoadServicesDescription')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addService = async (serviceData: any) => {
    if (!user || !userId || activeRole !== 'practitioner') {
      toast({
        variant: "destructive",
        title: t('servicesManagement.actions.authenticationRequired'),
        description: t('servicesManagement.actions.authenticationRequiredDescription')
      });
      return false;
    }

    try {
      setIsSubmitting(true);
      
      const { data, error } = await supabase
        .from('services')
        .insert({
          practitioner_id: userId,
          ...serviceData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select();

      if (error) {
        console.error("Insert error details:", error);
        throw error;
      }

      toast({
        title: t('servicesManagement.form.serviceAdded'),
        description: t('servicesManagement.form.serviceAddedDescription')
      });

      if (data && data.length > 0) {
        setServices([data[0] as Service, ...services]);
      }

      return true;
    } catch (error) {
      console.error('Error adding service:', error);
      toast({
        variant: "destructive",
        title: t('servicesManagement.form.failedToAddService'),
        description: t('servicesManagement.form.failedToAddServiceDescription')
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleServiceActive = async (serviceId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('services')
        .update({ 
          is_active: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', serviceId);

      if (error) {
        throw error;
      }

      setServices(services.map(service => 
        service.id === serviceId 
          ? { ...service, is_active: !currentStatus } 
          : service
      ));

      toast({
        title: !currentStatus ? t('servicesManagement.actions.serviceActivated') : t('servicesManagement.actions.serviceDeactivated'),
        description: !currentStatus ? t('servicesManagement.actions.serviceActivatedDescription') : t('servicesManagement.actions.serviceDeactivatedDescription')
      });
    } catch (error) {
      console.error('Error toggling service status:', error);
      toast({
        variant: "destructive",
        title: t('servicesManagement.actions.failedToUpdateService'),
        description: t('servicesManagement.actions.failedToUpdateServiceDescription')
      });
    }
  };

  const updateService = async (serviceId: string, serviceData: any) => {
    if (!user || !userId || activeRole !== 'practitioner') {
      toast({
        variant: "destructive",
        title: t('servicesManagement.actions.authenticationRequired'),
        description: t('servicesManagement.actions.authenticationRequiredDescription')
      });
      return false;
    }

    try {
      setIsSubmitting(true);
      
      const { data, error } = await supabase
        .from('services')
        .update({
          ...serviceData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', serviceId)
        .select();

      if (error) {
        console.error("Update error details:", error);
        throw error;
      }

      toast({
        title: t('servicesManagement.form.serviceUpdated'),
        description: t('servicesManagement.form.serviceUpdatedDescription')
      });

      if (data && data.length > 0) {
        setServices(services.map(service => 
          service.id === serviceId ? data[0] as Service : service
        ));
      }

      return true;
    } catch (error) {
      console.error('Error updating service:', error);
      toast({
        variant: "destructive",
        title: t('servicesManagement.form.failedToUpdateService'),
        description: t('servicesManagement.form.failedToUpdateServiceDescription')
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteService = async (serviceId: string) => {
    if (!window.confirm(t('servicesManagement.actions.confirmDelete'))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', serviceId);

      if (error) {
        throw error;
      }

      setServices(services.filter(service => service.id !== serviceId));

      toast({
        title: t('servicesManagement.actions.serviceDeleted'),
        description: t('servicesManagement.actions.serviceDeletedDescription')
      });
    } catch (error: any) {
      console.error('Error deleting service:', error);
      
      // Check if it's a foreign key constraint error (bookings exist)
      if (error.code === '23503' || error.message?.includes('foreign key')) {
        toast({
          variant: "destructive",
          title: t('servicesManagement.actions.cannotDeleteService'),
          description: t('servicesManagement.actions.serviceHasBookings')
        });
      } else {
        toast({
          variant: "destructive",
          title: t('servicesManagement.actions.failedToDeleteService'),
          description: t('servicesManagement.actions.failedToDeleteServiceDescription')
        });
      }
    }
  };

  useEffect(() => {
    fetchServices();
  }, [userId]);

  return {
    services,
    isLoading,
    isSubmitting,
    addService,
    updateService,
    toggleServiceActive,
    deleteService,
    fetchServices
  };
};
